import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid,
  Paper,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Stack,
  Autocomplete,
  TextField
} from '@mui/material';
import { Analytics as AnalyticsIcon } from '@mui/icons-material';
import NewSpeciesTable from '../components/NewSpeciesTable';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  LineChart, 
  Line,
  Legend
} from 'recharts';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

// Color palette for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF7C7C'];

const SpeciesAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [speciesData, setSpeciesData] = useState([]);
  const [availableSpecies, setAvailableSpecies] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState('');
  const [hourlyActivityData, setHourlyActivityData] = useState([]);
  const [speciesConfidenceData, setSpeciesConfidenceData] = useState([]);
  const [speciesTimelineData, setSpeciesTimelineData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [selectedSpeciesCount, setSelectedSpeciesCount] = useState(0);
  const [selectedSpeciesInfo, setSelectedSpeciesInfo] = useState(null);

  const fetchAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!supabase) {
        throw new Error('Supabase client not configured');
      }

      // Fetch all detection data with species information (paginate past Supabase's 1000 row default)
      const pageSize = 1000;
      let from = 0;
      let allData = [];
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('detection')
          .select(`
            confidence, 
            absolute_start_time,
            scientific_name,
            species (
              common_name,
              scientific_name,
              bow_url
            )
          `)
          .gte('confidence', 0.1)
          .order('absolute_start_time')
          .range(from, from + pageSize - 1);

        if (error) throw error;

        allData = allData.concat(data || []);
        hasMore = data && data.length === pageSize;
        from += pageSize;
      }

      setRawData(allData);
      processAnalyticsData(allData);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const processAnalyticsData = (data) => {
    // Process species distribution data
    const speciesCounts = {};
    data.forEach(d => {
      const commonName = d.species?.common_name;
      if (commonName) {
        speciesCounts[commonName] = (speciesCounts[commonName] || 0) + 1;
      }
    });
    
    const speciesArray = Object.entries(speciesCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 8)
      .map(([name, count]) => ({ name, count }));
    setSpeciesData(speciesArray);

    // Get available species for dropdown (all species)
    const allSpecies = Object.entries(speciesCounts)
      .sort(([,a], [,b]) => b - a)
      .map(([name]) => name);
    setAvailableSpecies(allSpecies);
    
    // Set default selected species to most common
    if (allSpecies.length > 0 && !selectedSpecies) {
      setSelectedSpecies(allSpecies[0]);
    }
  };

  const processSpeciesSpecificData = (data, species) => {
    if (!species) return;
    
    const speciesData = data.filter(d => d.species?.common_name === species);
    
    // Set total count for selected species
    setSelectedSpeciesCount(speciesData.length);
    
    // Set selected species info (including bow_url)
    if (speciesData.length > 0) {
      setSelectedSpeciesInfo(speciesData[0].species);
    }
    
    // Process hourly activity
    const hourCounts = Array.from({ length: 24 }, (_, i) => ({ hour: i, count: 0 }));
    speciesData.forEach(d => {
      const hour = new Date(d.absolute_start_time).getHours();
      hourCounts[hour].count++;
    });
    setHourlyActivityData(hourCounts);

    // Process confidence distribution for this species
    const confidenceBins = Array.from({ length: 10 }, (_, i) => ({
      range: `${(i/10).toFixed(1)}-${((i+1)/10).toFixed(1)}`,
      count: 0
    }));
    
    speciesData.forEach(d => {
      const binIndex = Math.min(Math.floor(d.confidence * 10), 9);
      confidenceBins[binIndex].count++;
    });
    setSpeciesConfidenceData(confidenceBins);

    // Process 30-day timeline for this species
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const recentData = speciesData.filter(d => new Date(d.absolute_start_time) >= thirtyDaysAgo);
    const dailyCounts = {};
    
    recentData.forEach(d => {
      const date = new Date(d.absolute_start_time).toISOString().split('T')[0];
      dailyCounts[date] = (dailyCounts[date] || 0) + 1;
    });
    
    const timelineArray = Object.entries(dailyCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({ date, detections: count }));
    setSpeciesTimelineData(timelineArray);
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Process species-specific data when species selection changes
  useEffect(() => {
    if (rawData.length > 0 && selectedSpecies) {
      processSpeciesSpecificData(rawData, selectedSpecies);
    }
  }, [rawData, selectedSpecies]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="h6">Loading species analytics...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Alert severity="error">Error loading analytics: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <AnalyticsIcon />
          </Avatar>
          <Typography variant="h4" fontWeight="bold">
            🦅 Species Analytics
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Detailed analysis of bird species behavior, identification patterns, and population insights.
        </Typography>
      </Box>

      {/* New Species Table */}
      <NewSpeciesTable supabase={supabase} />

      {/* Species Selection and Total Detections */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Autocomplete
          options={availableSpecies}
          value={selectedSpecies}
          onChange={(event, newValue) => setSelectedSpecies(newValue || '')}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Search Species" 
              variant="outlined"
              sx={{ minWidth: 300 }}
            />
          )}
          isOptionEqualToValue={(option, value) => option === value}
        />
        
        <Box sx={{ textAlign: 'right' }}>
          <Typography 
            variant="h3" 
            fontWeight="bold" 
            color="primary.main"
            sx={{ lineHeight: 1 }}
          >
            {selectedSpeciesCount.toLocaleString()}
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ fontWeight: 'medium' }}
          >
            Total Detections
          </Typography>
          {selectedSpeciesInfo?.bow_url && (
            <Box sx={{ mt: 1 }}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Learn more about this species:
              </Typography>
              <Typography
                component="a"
                href={selectedSpeciesInfo.bow_url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                🔗 Birds of the World (Cornell)
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Stack spacing={4} sx={{ width: '100%' }}>
        {/* Hourly Activity for Selected Species */}
        <Card sx={{ height: 500, width: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight="medium" gutterBottom>
              ⏰ {selectedSpecies} - Hourly Activity
            </Typography>
            <Box sx={{ width: '100%', height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourlyActivityData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="hour" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(hour) => `${hour}:00`}
                    interval={1}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Detections', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    labelFormatter={(hour) => `${hour}:00 - ${hour + 1}:00`}
                    formatter={(value) => [value, 'Detections']}
                  />
                  <Bar dataKey="count" fill="#82ca9d" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
        
        {/* Confidence Distribution for Selected Species */}
        <Card sx={{ height: 500, width: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight="medium" gutterBottom>
              🎯 {selectedSpecies} - ML Model Confidence Distribution
            </Typography>
            <Box sx={{ width: '100%', height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={speciesConfidenceData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="range" 
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Confidence Range', position: 'insideBottom', offset: -10 }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Detections', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8884d8" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* 30-Day Timeline for Selected Species */}
        <Card sx={{ height: 500, width: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight="medium" gutterBottom>
              📈 {selectedSpecies} - 30 Day Trend
            </Typography>
            <Box sx={{ width: '100%', height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={speciesTimelineData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString()}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Daily Detections', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    labelFormatter={(value) => new Date(value).toLocaleDateString()}
                    formatter={(value) => [value, 'Detections']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="detections" 
                    stroke="#ff7300" 
                    strokeWidth={3}
                    dot={{ r: 5 }}
                  />
                </LineChart>
                </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default SpeciesAnalytics;
